import React, { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { Area, AreaChart, CartesianGrid, Cell, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import merchantApi from '../../services/merchantApi'
import { buildWhatsAppCustomerMessage, destinationFromApi, downloadQrCode, formatDateTime, formatMoney, formatNumber, getApiItem, mapCampaignFromApi } from './campaignLinkUtils'
import Header from '../../components/Header/Header'
import './CampaignLinkDetailPage.scss'
import { Icon } from '@iconify/react/dist/iconify.js'

const defaultClickData = [
  { day: '30 Apr', clicks: 0 },
  { day: '1 May', clicks: 0 },
  { day: '2 May', clicks: 0 },
  { day: '3 May', clicks: 0 },
  { day: '4 May', clicks: 0 },
  { day: '5 May', clicks: 0 },
  { day: '6 May', clicks: 0 },
]

const defaultSourceData = [
  { name: 'Direct', value: 1 },
]

const asNumber = (value, fallback = 0) => {
  if (value === null || value === undefined || value === '') return fallback
  const cleaned = typeof value === 'string' ? value.replace(/[^0-9.-]/g, '') : value
  const number = Number(cleaned)
  return Number.isFinite(number) ? number : fallback
}

const formatPercent = (value) => {
  if (value === null || value === undefined || value === '') return null
  const number = asNumber(value)
  return `${Math.abs(number).toFixed(1)}%`
}

const renderTrend = (value, fallbackText) => {
  const trend = formatPercent(value)
  const positive = asNumber(value) >= 0

  return (
    <div className={`trend ${positive ? 'positive' : 'negative'}`}>
      {positive ? (asNumber(value) === 0 ? null : <Icon icon="ph:arrow-up" width={14} />) : <Icon icon="ph:arrow-down" width={14} />}
      {trend || fallbackText}
    </div>
  )
}

export default function CampaignLinkDetailPage() {
  const navigate = useNavigate()
  const { id } = useParams()
  const [campaign, setCampaign] = useState(null)
  const [analytics, setAnalytics] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [showMenu, setShowMenu] = useState(false)

  const loadCampaign = async () => {
    setLoading(true)
    setError('')
    try {
      const [campaignResponse, analyticsResponse] = await Promise.allSettled([
        merchantApi.campaignLinks.show(id),
        merchantApi.campaignLinks.analytics(id),
      ])

      if (campaignResponse.status === 'rejected') throw campaignResponse.reason
      setCampaign(mapCampaignFromApi(getApiItem(campaignResponse.value)))

      if (analyticsResponse.status === 'fulfilled') {
        setAnalytics(getApiItem(analyticsResponse.value))
      }
    } catch (err) {
      setError(err.message || 'Unable to load campaign link.')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadCampaign()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id])

  const kpis = useMemo(() => {
    const source = analytics?.summary || analytics || {}
    return [
      ['Clicks', source.clicks ?? campaign?.clicks ?? 0],
      ['Visitors', source.unique_visitors ?? campaign?.unique_visitors ?? 0],
      ['WA Opens', source.whatsapp_opens ?? source.wa_opens ?? campaign?.waOpens ?? 0],
      ['Orders', source.orders ?? campaign?.orders ?? 0],
      ['Revenue', formatMoney(source.revenue ?? campaign?.revenue ?? 0)],
    ]
  }, [analytics, campaign])

  const clickData = useMemo(() => {
    const rows = analytics?.clicks_over_time || analytics?.clicksOverTime || analytics?.daily_clicks || []
    if (!Array.isArray(rows) || rows.length === 0) return defaultClickData
    return rows.map((row) => ({
      day: row.date || row.day || row.label,
      clicks: row.total || row.count || row.value || 0,
    }))
  }, [analytics])

  const sourceData = useMemo(() => {
    const rows = analytics?.clicks_by_source || analytics?.clicksBySource || analytics?.sources || []
    if (!Array.isArray(rows) || rows.length === 0) return defaultSourceData
    return rows.map((row) => ({
      name: row.traffic_source || row.name || row.label,
      value: row.total || row.count || row.value || 0,
    })).filter((row) => row.value > 0)
  }, [analytics])

  const conversions = useMemo(() => {
    const source = analytics?.conversions || []
    if (Array.isArray(source) && source.length > 0) {
      return source.map((row) => [row.action || row.name || row.label, row.count || row.value || 0, row.rate || '-'])
    }
    return [
      ['WhatsApp Opens', analytics?.summary?.whatsapp_opens || 0, analytics?.summary?.whatsapp_open_rate || '-'],
      ['Orders', analytics?.summary?.orders || 0, analytics?.summary?.order_conversion_rate || '-'],
    ]
  }, [analytics, campaign])

  const copyText = async (value) => {
    try {
      await navigator.clipboard.writeText(value)
      alert('Copied to clipboard.')
    } catch (_) {
      alert(value)
    }
  }

  const handleDownloadQr = async () => {
    await downloadQrCode(campaign.smartLink, `${campaign.slug || campaign.name || 'campaign-link'}-qr.png`)
    setShowMenu(false)
  }

  const handleDelete = async () => {
    if (!window.confirm('Delete this campaign link?')) return
    await merchantApi.campaignLinks.remove(id)
    navigate('/marketing/links')
  }

  if (loading) return <div className="campaign-detail-page"><p>Loading campaign link...</p></div>
  if (error) return <div className="campaign-detail-page"><div className="campaign-error">{error}</div></div>
  if (!campaign) return <div className="campaign-detail-page"><p>Campaign link not found.</p></div>

  const customerFirstMessage = buildWhatsAppCustomerMessage(campaign.defaultWhatsAppText || campaign.whatsappDefaultText || campaign.whatsappMessage || '', campaign.trackingCode || campaign.slug)

  return (
    <div className="campaign-detail-page">
      <Header
        showBack={true}
        onBack={() => navigate('/marketing/links')}
        items={[
          { label: 'Marketing', to: '/' },
          { label: 'Campaign Links', to: '/marketing/links' },
          { label: campaign?.name || 'Campaign', current: true },
        ]}
      />
      <div className="campaign-detail-header">
        <div>
          <div className="campaign-detail-header-title">{campaign.name} <span className={`campaign-status campaign-status-${campaign.status.toLowerCase()}`}>{campaign.status}</span></div>
          <div className="campaign-detail-header-description">{campaign.description}</div>
        </div>
        <div className="campaign-header-actions">
          <button className="campaign-btn campaign-btn-light" type="button" onClick={() => navigate(`/marketing/links/${campaign.id}/edit`)}><Icon icon="ph:pencil-simple" width={18} /> Edit Link</button>
          <div className="campaign-more-wrapper">
            <button type="button" className="campaign-btn campaign-btn-light campaign-more-btn" onClick={() => setShowMenu((prev) => !prev)}><Icon icon="ph:dots-three" width={18} /></button>
            {showMenu && (
              <div className="campaign-more-menu">
                <button type="button" onClick={() => copyText(campaign.smartLink)}><Icon icon="ph:copy" width={16} /> Copy Smart Link</button>
                {campaign.whatsappLink && <button type="button" onClick={() => copyText(campaign.whatsappLink)}><Icon icon="ph:copy" width={16} /> Copy WhatsApp Link</button>}
                <button type="button" onClick={handleDownloadQr}><Icon icon="ph:qr-code" width={16} /> Download QR Code</button>
                {/* <button type="button"><ShareNetwork size={16} /> Share Link</button> */}
                {/* <button type="button" onClick={() => navigate(`/marketing/links/${campaign.id}`)}><ChartLineUp size={16} /> View Analytics</button> */}
                <button type="button" className="danger" onClick={handleDelete}><Icon icon="ph:trash" width={16} /> Delete Link</button>
              </div>
            )}
          </div>
        </div>
      </div>

      <section className="campaign-overview-card">
        <h2>Overview</h2>
        <div className="campaign-overview-grid">
          <div><span>Smart Link</span><strong>{campaign.smartLink}</strong></div>
          <div><span>Destination</span><strong>{destinationFromApi(campaign.destinationType)}</strong><small>{campaign.destinationDetail || '-'}</small></div>
          {campaign.destinationType === 'whatsapp_chat' && <div className="campaign-overview-message"><span>Customer First Message</span><strong>{customerFirstMessage || '-'}</strong><small>Pre-filled WhatsApp text plus WAVE tracking code after two blank lines.</small></div>}
          {campaign.destinationType === 'whatsapp_chat' && <div className="campaign-overview-message"><span>First Response Message</span><strong>{campaign.firstResponseMessage || '-'}</strong><small>Sent automatically by W.A.V.E. after the customer starts the conversation.</small></div>}
          <div><span>Source</span><strong>{campaign.source}</strong><small>{campaign.medium}</small></div>
          <div><span>Campaign Period</span><strong>{campaign.startAt ? formatDateTime(campaign.startAt) : 'Immediately'} to {campaign.endAt ? formatDateTime(campaign.endAt) : 'No end date'}</strong></div>
          <div><span>Created By</span><strong>{campaign.createdBy}</strong></div>
          <div><span>Status</span><span className={`campaign-status campaign-status-${campaign.status.toLowerCase()}`}>{campaign.status}</span></div>
        </div>
      </section>

      <div className="campaign-detail-kpis">
        {kpis.map(([label, value]) => <div key={label}><span>{label}</span><strong>{typeof value === 'number' ? formatNumber(value) : value}</strong><small>Live campaign data</small></div>)}
      </div>

      <div className="campaign-analytics-grid">
        <section className="campaign-chart-card campaign-chart-wide">
          <h2>Clicks Over Time</h2>
          <ResponsiveContainer width="100%" height={260}>
            <AreaChart data={clickData}>
              <defs><linearGradient id="clickFill" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#14b8c8" stopOpacity={0.28} /><stop offset="95%" stopColor="#14b8c8" stopOpacity={0} /></linearGradient></defs>
              <CartesianGrid strokeDasharray="4 4" vertical={false} />
              <XAxis dataKey="day" axisLine={false} tickLine={false} />
              <YAxis axisLine={false} tickLine={false} />
              <Tooltip />
              <Area type="monotone" dataKey="clicks" stroke="#14b8c8" strokeWidth={3} fill="url(#clickFill)" />
            </AreaChart>
          </ResponsiveContainer>
        </section>
        <section className="campaign-chart-card">
          <h2>Clicks by Source</h2>
          <ResponsiveContainer width="100%" height={260}>
            <PieChart><Pie data={sourceData} innerRadius={62} outerRadius={92} paddingAngle={2} dataKey="value">{sourceData.map((entry, index) => <Cell key={entry.name} fill={['#0f766e', '#22c55e', '#f97316', '#3b82f6', '#94a3b8'][index % 5]} />)}</Pie><Tooltip /></PieChart>
          </ResponsiveContainer>
        </section>
      </div>

      <div className="campaign-bottom-grid">
        <section className="campaign-table-card">
          <h2>Related Data</h2>
          {
            conversions.map(([action, count, rate]) =>
              <div className="campaign-conversion-row" key={action}>
                <span>{action}</span>
                <strong>{formatNumber(count)}</strong>
                <em>{renderTrend(rate)}</em>
              </div>
            )}
        </section>
        <section className="campaign-table-card">
          <h2>Quick Actions</h2>
          <div className="campaign-quick-actions">
            <button type="button" onClick={() => copyText(campaign.smartLink)}>
              <Icon icon="ph:copy" width={18} /> Copy Smart Link
            </button>
            <button type="button" onClick={() => copyText(campaign.whatsappLink)}><Icon icon="ph:copy" width={18} /> Copy WhatsApp Link</button>
            <button type="button" onClick={handleDownloadQr}><Icon icon="ph:qr-code" width={18} /> Download QR</button>
            {/* <button type="button"><ShareNetwork size={18} /> Share Link</button> */}
          </div>
        </section>
      </div>

      {/* <Link className="campaign-back-link" to="/marketing/links">Back to campaign links</Link> */}
    </div>
  )
}
